import { Button, Select } from "antd";
import { message } from "utils/ciq-message";
import MakeActiveWarningModal from "./active-version-warning-modal/make-active-warning-modal";
import { GridLoadingIndicator } from "components/widgets";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { useHistory, useLocation, useParams } from "react-router";
import { useSubscription } from "@apollo/client";
import { SUBSCRIPTION_GET_SCHEDULE_VERSIONS } from "services/graphQL/subscriptions";
import { getNewScheduleImpact } from "services/schedule-versions-services";
import { DateUtils } from "utils/dateutils";
import { Link } from "react-router-dom";
import ActiveTagIcon from "components/svg-icons/active-tag-icon";
import { ImpactSummaryDataType } from "./model";

import { TScheduleChange } from "../../../../context/ScheduleChangeContext";
import CompareScheduleV3 from "../compare-schedule-3";

export const DEFAULT_IMPACT_SUMMARY: ImpactSummaryDataType = {
  totalNoOfActivities: { updated: 0, deleted: 0, added: 0 },
  activitiesTiedToProcurement: { updated: 0, deleted: 0, added: 0 },
  impactedSubmittals: { updated: 0, deleted: 0, added: 0 },
  impactedMaterials: { updated: 0, deleted: 0, added: 0 }
};

export const THRESHOLD_REACHED_PERCENTAGE = 10;

type CompareRouteState = {
  selectedScheduleId?: string;
  autoSelectLatestUploaded?: boolean;
  uploadedScheduleName?: string;
  uploadedScheduleDate?: string;
};

/**
 * Picks the version the upload flow should land on: the one matching the name
 * and date the uploader passed through route state, else the most recently
 * created non-active version.
 */
function resolveVersionFromUploadFlow(
  scheduleVersions: Array<any>,
  uploadedScheduleName: string | undefined,
  uploadedScheduleDate: string | undefined
): string | undefined {
  const uploadedVersionByHint = scheduleVersions.find((version: any) => {
    const versionName = version?.schedule_name || "";
    const versionDate = version?.schedule_date
      ? DateUtils.format(version?.schedule_date)
      : "";
    return (
      !version?.is_active &&
      (!uploadedScheduleName || versionName === uploadedScheduleName) &&
      (!uploadedScheduleDate || versionDate === uploadedScheduleDate)
    );
  });
  const newestNonActiveVersion = scheduleVersions
    .filter((version: any) => !version?.is_active)
    .sort(
      (v1: any, v2: any) =>
        new Date(v2?.import_log?.created_at || v2?.created_at || 0).getTime() -
        new Date(v1?.import_log?.created_at || v1?.created_at || 0).getTime()
    )[0];
  return (
    uploadedVersionByHint?.import_log?.id ||
    newestNonActiveVersion?.import_log?.id
  );
}

function CompareSchedule() {
  const { Option } = Select;
  const [scheduleList, setScheduleList] = useState([] as Array<any>);
  const [activeVersion, setActiveVersion] = useState("");
  const [selectedVersion, setSelectedVersion] = useState<string>();
  const [loadedCompareVersion, setLoadedCompareVersion] = useState("");
  const [scheduleImpactSummary, setScheduleImpactSummary] =
    useState<ImpactSummaryDataType>(DEFAULT_IMPACT_SUMMARY);

  const loadingCompareData = false;

  const [isMakingActiveLoading, setMakingActiveLoading] = useState(false);
  const [isMakeScheduleActiveModalOpen, setMakeScheduleActiveModalOpen] =
    useState(false);
  const [skipSubscribe, setSkipSubscribe] = useState(true);
  const { tokenRetrievalState, gqlClientForProject, isProjectArchived } =
    useContext(ProjectContext);

  const [scheduleChange, setScheduleChange] = useState<TScheduleChange>({
    params: {
      importLogId: selectedVersion,
      sourceLogId: activeVersion,
      projectToken: tokenRetrievalState?.token
    },
    searchText: ""
  });
  const [isLoadingData, setIsLoadingData] = useState({ current: false });
  const { search, state } = useLocation<CompareRouteState>();
  const history = useHistory();

  const { data: scheduleListData, loading } = useSubscription(
    SUBSCRIPTION_GET_SCHEDULE_VERSIONS,
    {
      client: gqlClientForProject,
      skip: skipSubscribe
    }
  );

  useEffect(() => {
    setTimeout(() => {
      // CIQ-292
      setSkipSubscribe(false);
    }, 10);
  }, []);

  const selectedVersionId =
    new URLSearchParams(search).get("selectedVersionId") ||
    state?.selectedScheduleId;
  const shouldAutoSelectUploaded = Boolean(state?.autoSelectLatestUploaded);

  const { projectId } = useParams<{ projectId: string }>();

  useEffect(() => {
    if (scheduleListData) {
      const scheduleVersions = scheduleListData.schedule_import_details;
      setScheduleList(scheduleVersions);
      const activeVersionTemp = scheduleVersions.find((e: any) => e.is_active);
      const selectedVersionFromUploadFlow = shouldAutoSelectUploaded
        ? resolveVersionFromUploadFlow(
            scheduleVersions,
            state?.uploadedScheduleName,
            state?.uploadedScheduleDate
          )
        : undefined;
      const routeSelectedVersionId =
        selectedVersionId || selectedVersionFromUploadFlow;

      if (routeSelectedVersionId) {
        setSelectedVersion(routeSelectedVersionId);
      }

      if (activeVersionTemp) {
        setActiveVersion(activeVersionTemp.import_log.id);
        // If already selected version then no need to set again
        if (!routeSelectedVersionId) {
          setSelectedVersion((prev) => prev || activeVersionTemp.import_log.id);
        }
      } else if (!routeSelectedVersionId) {
        const firstSchedule = scheduleVersions[0];
        if (firstSchedule?.import_log?.id) {
          setSelectedVersion(firstSchedule.import_log.id);
        }
      }
    }
  }, [
    scheduleListData,
    selectedVersionId,
    shouldAutoSelectUploaded,
    state?.uploadedScheduleDate,
    state?.uploadedScheduleName
  ]);

  const loadComparisonData = () => {
    // Both callers (the gated useEffect and the Compare button rendered only
    // when selectedVersion is truthy) guarantee selectedVersion is set here.
    /* v8 ignore next */
    if (!selectedVersion) return;
    setLoadedCompareVersion(selectedVersion);
    const toDispatch = {
      params: {
        importLogId: selectedVersion,
        sourceLogId: activeVersion,
        projectToken: tokenRetrievalState?.token
      },
      searchText: ""
    };

    setScheduleChange(toDispatch);
  };

  const resetToInitialState = () => {
    setLoadedCompareVersion("");
    setScheduleImpactSummary(DEFAULT_IMPACT_SUMMARY);
  };

  const formatScheduleName = (schedule: any): string | undefined => {
    if (!schedule) return undefined;
    return schedule.schedule_date
      ? `${DateUtils.format(schedule.schedule_date)} — ${schedule.schedule_name}`
      : schedule.schedule_name;
  };

  const fullSelectedScheduleName = useMemo(() => {
    const selectedScheduleDetails = scheduleList.find((schedule: any) => {
      return schedule.import_log.id === selectedVersion;
    });
    return formatScheduleName(selectedScheduleDetails);
  }, [scheduleList, selectedVersion]);

  const fullSourceScheduleName = useMemo(() => {
    const sourceScheduleDetails = scheduleList.find((schedule: any) => {
      return schedule.import_log.id === activeVersion;
    });
    return formatScheduleName(sourceScheduleDetails);
  }, [scheduleList, activeVersion]);

  const handleProceedTosetActive = async () => {
    // If there's no current active schedule, we can't safely compute impacts
    // against a missing "source" version.
    // Unreachable: only invoked via the modal, opened by a button rendered only
    // when both activeVersion and selectedVersion are truthy.
    /* v8 ignore next */
    if (!activeVersion || !selectedVersion) return;
    setMakingActiveLoading(true);

    const scheduleImpactResponse: any = await getNewScheduleImpact(
      selectedVersion,
      tokenRetrievalState?.token,
      true
    );

    if (scheduleImpactResponse?.data?.success) {
      message.success(`${fullSelectedScheduleName} is now the active version`);
      resetToInitialState();
      setMakeScheduleActiveModalOpen(false);
      history.replace(`/project/${projectId}/schedule/change-impact`);
    } else {
      message.error(
        scheduleImpactResponse?.message || "Failed to make active version"
      );
    }
    setMakingActiveLoading(false);
  };

  useEffect(() => {
    if (
      activeVersion &&
      selectedVersion &&
      scheduleListData &&
      (selectedVersionId || shouldAutoSelectUploaded)
    ) {
      loadComparisonData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVersionId,
    selectedVersion,
    activeVersion,
    scheduleListData,
    shouldAutoSelectUploaded
  ]);

  const notFoundContent = (
    <div className="text-center">
      There are no schedules to compare with.
      <Link
        to={`/project/${projectId}/schedule/project-schedules`}
        data-testid="index-link"
      >
        {" "}
        Click here{" "}
      </Link>
      to upload more schedule versions.
    </div>
  );

  return (
    <div className="flex flex-col h-full pt-[10px]">
      <div className="flex justify-between px-10">
        <div className="flex items-center">
          <div>
            <span className="text-md font-medium text-ciq-gray-900 opacity-85">
              Select another version:
            </span>
            <Select
              className="w-[400px] ml-1"
              loading={loading}
              value={selectedVersion}
              onChange={(e: string) => {
                setSelectedVersion(e);
                setScheduleImpactSummary(DEFAULT_IMPACT_SUMMARY);
                if (selectedVersionId || shouldAutoSelectUploaded) {
                  history.push(`/project/${projectId}/schedule/change-impact`);
                }
              }}
              notFoundContent={notFoundContent}
              data-testid="index-select-schedule"
            >
              {scheduleList.map((version: any) => {
                const scheduleName = version?.schedule_date
                  ? `${DateUtils.format(version?.schedule_date)}-${
                      version?.schedule_name
                    }`
                  : version?.schedule_name || "";
                return (
                  <Option
                    key={version?.import_log?.id}
                    value={version?.import_log?.id}
                    placeholder="Select Schedule Version"
                    disabled={version?.is_active}
                    data-testid={`schedule-option-${version?.import_log?.id}`}
                  >
                    <div className="flex space-x-2 items-center">
                      <span className="grow-flex truncate" title={scheduleName}>
                        {scheduleName}
                      </span>
                      {version?.is_active ? (
                        <ActiveTagIcon className="shrink-0" />
                      ) : null}
                    </div>
                  </Option>
                );
              })}
            </Select>
          </div>
          {activeVersion &&
          selectedVersion &&
          activeVersion !== selectedVersion ? (
            <div className="flex px-2 space-x-2 items-center">
              {loadedCompareVersion !== selectedVersion ? (
                <Button
                  onClick={loadComparisonData}
                  className="mx-2"
                  data-testid="index-button-compare"
                >
                  Compare
                </Button>
              ) : (
                <Button
                  className="mx-2"
                  onClick={() => {
                    setMakeScheduleActiveModalOpen(true);
                  }}
                  loading={isLoadingData.current}
                  disabled={!!isProjectArchived}
                  data-testid="index-button-make-active"
                >
                  Make this the active version
                </Button>
              )}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-col h-full">
        {loading ? (
          <div
            className="w-full h-full flex items-center justify-center"
            data-testid="schedule-loading-indicator"
          >
            <GridLoadingIndicator />
          </div>
        ) : loadedCompareVersion !== selectedVersion ? (
          <div
            className="w-full h-full flex items-center justify-center"
            data-testid="schedule-not-found-content"
          >
            {scheduleList.length > 0
              ? " Please select another version to compare to"
              : notFoundContent}
          </div>
        ) : /* loadingCompareData is the constant `false`, so this branch is dead */
        /* v8 ignore next */
        loadingCompareData ? (
          <div className="w-full h-full flex items-center justify-center">
            <GridLoadingIndicator />
          </div>
        ) : (
          <CompareScheduleV3
            scheduleChange={scheduleChange}
            isLoadingData={isLoadingData}
            setIsLoadingData={setIsLoadingData}
            onScheduleImpactSummaryChange={setScheduleImpactSummary}
            sourceScheduleName={fullSourceScheduleName}
            importScheduleName={fullSelectedScheduleName}
          />
        )}
      </div>

      <MakeActiveWarningModal
        scheduleImpactSummary={scheduleImpactSummary}
        fullSelectedScheduleName={fullSelectedScheduleName}
        isMakeScheduleActiveModalOpen={isMakeScheduleActiveModalOpen}
        setMakeScheduleActiveModalOpen={setMakeScheduleActiveModalOpen}
        handleProceedTosetActive={handleProceedTosetActive}
        isMakingActiveLoading={isMakingActiveLoading}
      />
    </div>
  );
}

export default CompareSchedule;
